import {
  Alert,
  Badge,
  Button,
  Grid,
  Group,
  Modal,
  NavLink,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconLock } from "@tabler/icons-react";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { z } from "zod";
import { usePermission } from "@how/app-kit";
import type { Enums } from "@how/database";
import { semanticColor } from "@how/design-system";
import {
  ConfirmActionModal,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  useSearchParamState,
} from "@how/ui";
import { CommunicationsTabs } from "./CommunicationsTabs";
import {
  AddPersonError,
  type Conversation,
  type InboxFilter,
  type ThreadMessage,
  useAddPersonFromConversation,
  useInboxConversations,
  useInboxThread,
  useMarkRead,
  useReply,
  useSetConfidential,
  useSetHandled,
  useUnknownNumber,
} from "./inbox-queries";
import { formatPhone } from "./format-phone";

const FILTERS: { value: InboxFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "unread", label: "Unread" },
  { value: "handled", label: "Handled" },
  { value: "opted_out", label: "Opt-outs" },
];

const EMPTY: Record<InboxFilter, string> = {
  all: "No texts here yet.",
  unread: "Nothing unread.",
  handled: "Nothing handled.",
  opted_out: "No one has opted out.",
};

const isFilter = (value: string): value is InboxFilter => FILTERS.some((f) => f.value === value);

const when = (iso: string | null) =>
  iso === null
    ? ""
    : new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/** How each message went, in words; typed on the enum so a new status fails to compile until it has words. */
const STATUS_WORDS: Record<Enums<"message_status">, string> = {
  queued: "Sending",
  sent: "Sent",
  delivered: "Delivered",
  read: "Read",
  failed: "Failed",
  blocked: "Not sent",
  cancelled: "Not sent",
};
const statusWords = (status: string) =>
  status in STATUS_WORDS ? STATUS_WORDS[status as Enums<"message_status">] : status;

/** Why a text was held back, in the church's words (the codes are the database's). */
const REASON_WORDS: Record<string, string> = {
  no_consent: "They replied STOP or no longer agree to texts, so it wasn't sent.",
  minor: "They're a child, and HoW never texts a child, so it wasn't sent.",
  no_person: "Their record was removed or merged, so it wasn't sent.",
  no_connection: "Texting is turned off for your church, so it wasn't sent.",
  no_address: "There's no mobile number on their record, so it wasn't sent.",
  not_pilot_tester:
    "Pilot texting is on, and their number hasn't opted in to the pilot, so it wasn't sent.",
  interrupted:
    "Sending was interrupted, so it may not have arrived. Check with them before sending it again.",
};

/** Why the website's Text us sent no confirmation, in the church's words (record_text_us's codes, ADR 0074). */
const WEB_REASON_WORDS: Record<string, string> = {
  queued: "A confirmation text went to them automatically.",
  needs_decision:
    "We couldn't tell for sure who this is, so it isn't linked to anyone and no text was sent. Use Add this person.",
  minor: "Their record is a child's, and HoW never texts a child, so no text was sent.",
  not_main_number:
    "Their record is texted at another number, so their yes to texts waits for you and no text was sent.",
  outside_us: "It isn't a US or Canadian number, so no text was sent.",
  no_connection: "Texting is turned off for your church, so no text was sent.",
  not_pilot_tester:
    "Pilot texting is on, and their number hasn't opted in to the pilot (not a pilot tester), so no text was sent.",
  recent: "A confirmation went to this number a few minutes ago, so no second one was sent.",
};

/**
 * What a note from the website's Text us says: where it came from, whether they asked for texts, and whether a
 * confirmation went or why not. The visitor was told none of this beyond "your message is with our team".
 */
export function webNoteWords(
  page: string,
  consent: string | null,
  reason: string | null,
): { readonly title: string; readonly consent: string; readonly outcome: string } {
  const path = (() => {
    try {
      const url = new URL(page);
      return `${url.pathname}${url.search}`;
    } catch {
      return "";
    }
  })();
  const asked = consent === "consent";
  return {
    title: path === "" ? "From your website's Text us" : `From your website's Text us, on ${path}`,
    consent: asked
      ? "They ticked the box to be texted back."
      : "They didn't tick the box for texts.",
    outcome:
      reason === "no_consent"
        ? asked
          ? "They don't agree to texts now (or replied STOP), so no text was sent."
          : "No text was sent. Follow up another way, or ask them to text the church."
        : (WEB_REASON_WORDS[reason ?? ""] ?? ""),
  };
}

/**
 * Why a text from the church didn't go or didn't arrive, or null. A held-back answer says why in words; a refusal from
 * the carrier or Twilio is given in their words.
 */
export function reasonWords(status: string | null, reason: string | null): string | null {
  if (reason === null || reason === "" || (status !== "failed" && status !== "blocked"))
    return null;
  return REASON_WORDS[reason] ?? `It couldn't be delivered. Twilio said: ${reason}`;
}

/** Re-exported for the screens and tests that read it from here. */
export { formatPhone };

const NO_LONGER_YOURS = "This conversation is no longer available to you.";
const HIDDEN_BY_MARK = "Marked confidential. Only people with care access can read it now.";

/**
 * Communications → Inbox (#314): the texts people send the church, and the church's answers. Conversations on one
 * side, the chosen one's thread on the other (one at a time on a phone). Who may read, mark and answer is the
 * database's (`communications.inbox`, D20, D21); the screen says what it decided and why.
 */
export function InboxPage() {
  return (
    <>
      <PageHeader
        title="Communications"
        description="Who the church can reach, what it says, and what it has sent."
      />
      <CommunicationsTabs active="inbox" />
      <Inbox />
    </>
  );
}

function Inbox() {
  const [show, setShow] = useSearchParamState("show", "all");
  const filter: InboxFilter = isFilter(show) ? show : "all";
  const [selectedId, setSelectedId] = useSearchParamState("c");
  const [notice, setNotice] = useState<string | null>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  const phone = useMediaQuery("(max-width: 48em)");
  const conversations = useInboxConversations(filter);
  const rows = conversations.data?.pages.flat() ?? [];
  const listed = rows.find((c) => c.id === selectedId) ?? null;
  // The open conversation stays open when it leaves the list: read under Unread, opened again under Handled. The
  // last row seen for it is kept until another is chosen or it is hidden (#353 review).
  const [kept, setKept] = useState<Conversation | null>(null);
  useEffect(() => {
    if (listed !== null) setKept(listed);
  }, [listed]);
  // What this screen just changed (handled, confidential) shows at once, before the list is read again.
  const [patch, setPatch] = useState<{ id: string; changes: Partial<Conversation> } | null>(null);
  const row = listed ?? (kept !== null && kept.id === selectedId ? kept : null);
  const selected =
    row === null
      ? null
      : { ...row, ...(patch !== null && patch.id === row.id ? patch.changes : {}) };

  // When the conversation is hidden from the person who was reading it, focus goes to the notice saying why.
  // It is drawn once the selection has cleared, which goes through the address a moment later.
  const showingNotice = notice !== null && selected === null;
  useEffect(() => {
    if (showingNotice) noticeRef.current?.focus();
  }, [showingNotice]);

  // Back to conversations on a phone removes itself: focus goes to the row that was open, or, when that row has left
  // the list, to the Show control (#353 review).
  const showRef = useRef<HTMLDivElement>(null);
  const [backFrom, setBackFrom] = useState<string | null>(null);
  useEffect(() => {
    if (backFrom === null || selected !== null || conversations.isPending) return;
    const row = document.querySelector<HTMLElement>(`[data-conversation-id="${backFrom}"]`);
    (row ?? showRef.current?.querySelector<HTMLElement>("input:checked"))?.focus();
    setBackFrom(null);
  }, [backFrom, selected, conversations.isPending]);

  const hide = (message: string) => {
    setKept(null);
    setPatch(null);
    setSelectedId(undefined);
    setNotice(message);
  };

  const list = (
    <Stack gap="sm">
      <SegmentedControl
        ref={showRef}
        aria-label="Show"
        fullWidth
        value={filter}
        onChange={(value) => {
          setShow(value);
        }}
        data={FILTERS}
      />
      {conversations.isPending ? (
        <LoadingState rows={4} />
      ) : conversations.isError ? (
        <ErrorState message="Could not load the inbox. Please try again." />
      ) : rows.length === 0 ? (
        <EmptyState title={EMPTY[filter]} />
      ) : (
        <>
          <Stack
            component="ul"
            aria-label="Conversations"
            gap={4}
            m={0}
            p={0}
            style={{ listStyle: "none" }}
          >
            {rows.map((c) => (
              <li key={c.id}>
                <ConversationRow
                  conversation={c}
                  active={c.id === selectedId}
                  onOpen={() => {
                    setNotice(null);
                  }}
                />
              </li>
            ))}
          </Stack>
          {conversations.hasNextPage ? (
            <Button
              variant="subtle"
              loading={conversations.isFetchingNextPage}
              onClick={() => {
                void conversations.fetchNextPage();
              }}
            >
              Show more
            </Button>
          ) : null}
        </>
      )}
    </Stack>
  );

  const pane =
    selected === null ? (
      <Stack gap="sm">
        {notice === null ? null : (
          <Alert ref={noticeRef} tabIndex={-1} color={semanticColor("info")} variant="light">
            {notice}
          </Alert>
        )}
        {selectedId !== "" && !conversations.isPending ? (
          <Text size="sm" c="dimmed">
            That conversation isn't on this page of the list. Open it from the list.
          </Text>
        ) : (
          <Text size="sm" c="dimmed">
            Choose a conversation to read it.
          </Text>
        )}
      </Stack>
    ) : (
      <Thread
        key={selected.id}
        conversation={selected}
        onChanged={(changes) => {
          setPatch({
            id: selected.id,
            changes: { ...(patch?.id === selected.id ? patch.changes : {}), ...changes },
          });
        }}
        onBack={
          phone
            ? () => {
                setBackFrom(selected.id);
                setSelectedId(undefined);
              }
            : undefined
        }
        onHidden={hide}
      />
    );

  if (phone)
    return selected === null ? (
      <Stack gap="md">
        {pane}
        {list}
      </Stack>
    ) : (
      pane
    );
  return (
    <Grid gutter="lg">
      <Grid.Col span={5}>{list}</Grid.Col>
      <Grid.Col span={7}>{pane}</Grid.Col>
    </Grid>
  );
}

/**
 * A conversation in the list: a link to it (`?c=`), so it opens in a new tab too, and a NavLink, so it takes the
 * theme's four navigation states. Its name for a screen reader is short, visible words first.
 */
function ConversationRow({
  conversation: c,
  active,
  onOpen,
}: {
  conversation: Conversation;
  active: boolean;
  onOpen: () => void;
}) {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set("c", c.id);
  return (
    <NavLink
      component={Link}
      to={{ search: `?${next.toString()}` }}
      active={active}
      data-conversation-id={c.id}
      aria-current={active ? "true" : undefined}
      aria-label={[c.displayName, when(c.lastAt), ...(c.isUnread ? ["unread"] : [])].join(", ")}
      onClick={onOpen}
      label={
        <Group component="span" justify="space-between" wrap="nowrap" gap="xs">
          <Text component="span" size="sm" fw={c.isUnread ? 700 : 500} truncate>
            {c.displayName}
          </Text>
          <Text
            component="span"
            size="xs"
            c="dimmed"
            className="how-numeric"
            style={{ flexShrink: 0 }}
          >
            {when(c.lastAt)}
          </Text>
        </Group>
      }
      description={
        <Stack component="span" gap={4}>
          <Text component="span" size="sm" c="dimmed" lineClamp={1}>
            {c.lastBody ?? ""}
          </Text>
          {c.isUnread || c.isConfidential || c.isOptedOut || c.isHandled ? (
            <Group component="span" gap={4}>
              {c.isUnread ? (
                <Badge component="span" size="xs" variant="light" color={semanticColor("info")}>
                  Unread
                </Badge>
              ) : null}
              {c.isConfidential ? <ConfidentialBadge size="xs" /> : null}
              {c.isOptedOut ? (
                <Badge component="span" size="xs" variant="light" color={semanticColor("danger")}>
                  Opted out
                </Badge>
              ) : null}
              {c.isHandled ? (
                <Badge component="span" size="xs" variant="light" color={semanticColor("neutral")}>
                  Handled
                </Badge>
              ) : null}
            </Group>
          ) : null}
        </Stack>
      }
    />
  );
}

/** Confidential as Care shows it: the `special` role with a lock, so it reads as the same idea and not by colour alone. */
function ConfidentialBadge({ size }: { size: "xs" | "sm" }) {
  return (
    <Badge
      component="span"
      size={size}
      variant="light"
      color={semanticColor("special")}
      leftSection={<IconLock size={10} />}
    >
      Confidential
    </Badge>
  );
}

function Thread({
  conversation: c,
  onChanged,
  onBack,
  onHidden,
}: {
  conversation: Conversation;
  onChanged: (patch: Partial<Conversation>) => void;
  onBack: (() => void) | undefined;
  onHidden: (message: string) => void;
}) {
  const thread = useInboxThread(c.id);
  const markRead = useMarkRead();
  const setHandled = useSetHandled();
  const setConfidential = useSetConfidential();
  const careAccess = usePermission("care.read_confidential");
  const peopleWrite = usePermission("people.write", { write: true });
  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const marked = useRef(false);
  const focusHeading = () => {
    heading.current?.focus();
  };

  // Opening an unread conversation reads it, once.
  useEffect(() => {
    if (c.isUnread && !marked.current) {
      marked.current = true;
      markRead.mutate(c.id);
    }
  }, [c.id, c.isUnread, markRead]);
  // On a phone the row that opened this is gone (one pane at a time), so focus starts at the name. On a wider screen
  // the row stays, and focus stays on it.
  const onPhone = onBack !== undefined;
  useEffect(() => {
    if (onPhone) focusHeading();
  }, [onPhone]);

  // A conversation that is "not found" was made confidential or removed meanwhile: say so, rather than the raw error.
  const lost = (error: Error) => {
    if ((error as { code?: string }).code === "P0002") onHidden(NO_LONGER_YOURS);
  };
  const busy = setHandled.isPending || setConfidential.isPending;
  const error = setHandled.error ?? setConfidential.error;

  const markConfidential = () => {
    setConfidential.mutate(
      { conversationId: c.id, confidential: true },
      {
        onSuccess: () => {
          // Without care access the conversation is no longer theirs to read (D21).
          if (careAccess.data !== true) onHidden(HIDDEN_BY_MARK);
          else {
            onChanged({ isConfidential: true });
            focusHeading();
          }
        },
        onError: lost,
      },
    );
  };

  // The last thing they sent says why they opted out: a STOP text, or a "no" recorded on their record.
  const lastIn = [...(thread.data ?? [])].reverse().find((m) => m.kind === "in");
  const repliedStop = lastIn?.isOptOut === true;

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      component="section"
      aria-label={`Conversation with ${c.displayName}`}
    >
      <Stack gap="md">
        {onBack === undefined ? null : (
          <Button variant="subtle" size="xs" onClick={onBack} style={{ alignSelf: "flex-start" }}>
            Back to conversations
          </Button>
        )}
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
          <Stack gap={4}>
            <Title order={2} size="h4" ref={heading} tabIndex={-1}>
              {c.displayName}
            </Title>
            <Group gap={4}>
              {c.isConfidential ? <ConfidentialBadge size="sm" /> : null}
              {c.isOptedOut ? (
                <Badge size="sm" variant="light" color={semanticColor("danger")}>
                  Opted out
                </Badge>
              ) : null}
              {c.isHandled ? (
                <Badge size="sm" variant="light" color={semanticColor("neutral")}>
                  Handled
                </Badge>
              ) : null}
            </Group>
          </Stack>
          {/* Never disabled while working, so focus stays put; a second press waits for the first (UI standards). */}
          <Group gap="xs" wrap="wrap">
            <Button
              variant="default"
              size="xs"
              aria-pressed={c.isHandled}
              onClick={() => {
                if (busy) return;
                const handled = !c.isHandled;
                setHandled.mutate(
                  { conversationId: c.id, handled },
                  {
                    onSuccess: () => {
                      onChanged({ isHandled: handled });
                    },
                    onError: lost,
                  },
                );
              }}
            >
              {c.isHandled ? "Open again" : "Mark handled"}
            </Button>
            {!c.isConfidential ? (
              <Button
                variant="default"
                size="xs"
                onClick={() => {
                  if (busy) return;
                  // Without care access the mark can't be undone and hides the conversation: ask first.
                  if (careAccess.data === true) markConfidential();
                  else setConfirming(true);
                }}
              >
                Mark confidential
              </Button>
            ) : careAccess.data === true ? (
              <Button
                variant="default"
                size="xs"
                onClick={() => {
                  if (busy) return;
                  setConfidential.mutate(
                    { conversationId: c.id, confidential: false },
                    {
                      onSuccess: () => {
                        onChanged({ isConfidential: false });
                        focusHeading();
                      },
                      onError: lost,
                    },
                  );
                }}
              >
                Remove confidential
              </Button>
            ) : null}
          </Group>
        </Group>
        {error === null || (error as { code?: string }).code === "P0002" ? null : (
          <Alert color={semanticColor("danger")} variant="light">
            {error.message}
          </Alert>
        )}

        {thread.isPending ? (
          <LoadingState rows={3} />
        ) : thread.isError ? (
          <ErrorState message="Could not load this conversation. Please try again." />
        ) : (
          <Stack gap="xs">
            {thread.data.map((m, i) => (
              <Message key={`${m.at}-${String(i)}`} message={m} name={c.displayName} />
            ))}
          </Stack>
        )}

        <Answer
          conversation={c}
          repliedStop={repliedStop}
          canAdd={peopleWrite.data === true}
          onAdd={() => {
            setAdding(true);
          }}
        />
      </Stack>
      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          setConfirming(false);
          markConfidential();
        }}
        title="Mark confidential"
        message={`Mark the conversation with ${c.displayName} confidential? Only people with care access will be able to read it, and you won't be able to read it afterwards.`}
        confirmLabel="Mark confidential"
      />
      <Modal
        opened={adding}
        onClose={() => {
          setAdding(false);
        }}
        title="Add this person"
      >
        {adding ? (
          <AddPerson
            conversationId={c.id}
            onDone={(added) => {
              setAdding(false);
              if (added === null) return;
              // Shown at once, also when the conversation has left the list and the pane runs on the row last seen
              // (#353 review). The Add button is gone once they are linked: focus goes back to the conversation's name.
              onChanged({ isUnknown: false, personId: added.personId, displayName: added.name });
              setTimeout(focusHeading, 0);
            }}
          />
        ) : null}
      </Modal>
    </Paper>
  );
}

/** One text: theirs on one side, the church's (a campaign or an answer) on the other, with when and how it went. */
function Message({ message: m, name }: { message: ThreadMessage; name: string }) {
  if (m.kind === "web") return <WebNote message={m} />;
  const theirs = m.kind === "in";
  const who = theirs
    ? name
    : m.kind === "campaign"
      ? "Campaign"
      : m.kind === "auto"
        ? "Automatic reply"
        : "Answer";
  const why = theirs ? null : reasonWords(m.status, m.reason);
  return (
    <Paper
      component="article"
      withBorder
      radius="md"
      p="sm"
      maw="85%"
      style={{ alignSelf: theirs ? "flex-start" : "flex-end" }}
      {...(theirs ? {} : { bg: "var(--how-surface-sunken)" })}
    >
      <Group justify="space-between" gap="xs" wrap="wrap">
        <Text size="xs" fw={600}>
          {who}
        </Text>
        <Text size="xs" c="dimmed" className="how-numeric">
          {when(m.at)}
          {!theirs && m.status !== null ? ` · ${statusWords(m.status)}` : ""}
        </Text>
      </Group>
      <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
        {m.body}
      </Text>
      {why === null ? null : (
        <Text size="xs" mt={4}>
          {why}
        </Text>
      )}
      {m.isOptOut ? (
        <Badge size="xs" mt={4} variant="light" color={semanticColor("danger")}>
          Stopped texts
        </Badge>
      ) : null}
    </Paper>
  );
}

/** A message's arrival through the website's Text us (ADR 0074): a note across the thread, not a text. */
function WebNote({ message: m }: { message: ThreadMessage }) {
  const words = webNoteWords(m.body, m.status, m.reason);
  return (
    <Paper
      component="aside"
      radius="md"
      p="xs"
      bg="var(--how-surface-sunken)"
      aria-label="From your website's Text us"
    >
      <Group justify="space-between" gap="xs" wrap="wrap">
        <Text size="xs" fw={600}>
          {words.title}
        </Text>
        <Text size="xs" c="dimmed" className="how-numeric">
          {when(m.at)}
        </Text>
      </Group>
      <Text size="xs">{words.consent}</Text>
      {words.outcome === "" ? null : <Text size="xs">{words.outcome}</Text>}
    </Paper>
  );
}

/** Why a conversation can't be answered right now: what is missing, and how it gets fixed. Null when it can be. */
function answerBlock(
  c: Conversation,
  repliedStop: boolean,
): { readonly missing: string; readonly fix: string } | null {
  if (c.isOptedOut && c.isUnknown)
    return {
      missing: "This number replied STOP, so it can't be texted.",
      fix: "To be texted again, they text START to the church's number. You can still add them to your people.",
    };
  if (c.isOptedOut && repliedStop)
    return {
      missing: `${c.displayName} replied STOP, so they can't be texted.`,
      fix: "To be texted again, they text START to the church's number. Staff can't undo a STOP for them.",
    };
  if (c.isOptedOut)
    return {
      missing: `${c.displayName} has opted out of texts (their record says no to texts), so they can't be texted.`,
      fix: "Only their own new agreement changes that: when they tell you they want texts, open them in People and choose Opt in beside Text messages.",
    };
  if (c.isUnknown)
    return {
      missing: "This number matches nobody in your church.",
      fix: "Add this person, then record their agreement to texts on their record, to answer and to keep what they send on their record.",
    };
  if (!c.canReply)
    return {
      missing: `${c.displayName} can't be texted from here: they haven't agreed to texts, or they're a child, who is never messaged.`,
      fix: "If they've told you they want texts from the church, open them in People and choose Opt in beside Text messages in the Communication section.",
    };
  return null;
}

/**
 * The answer box. When the conversation can't be answered (an unknown number, a STOP, a "no" on their record, no
 * agreement, or a child), the box stays in place but turned off, with what is missing and how it gets fixed. The
 * database decides; this says what it decided.
 */
function Answer({
  conversation: c,
  repliedStop,
  canAdd,
  onAdd,
}: {
  conversation: Conversation;
  repliedStop: boolean;
  canAdd: boolean;
  onAdd: () => void;
}) {
  const reply = useReply();
  const [body, setBody] = useState("");
  const blocked = answerBlock(c, repliedStop);
  const text = body.trim();
  return (
    <Stack gap="xs">
      {blocked === null ? null : (
        <Alert color={semanticColor("warning")} variant="light" title="You can't answer yet">
          {blocked.missing}
        </Alert>
      )}
      {c.isUnknown && canAdd ? (
        <Button variant="light" onClick={onAdd} style={{ alignSelf: "flex-start" }}>
          Add this person
        </Button>
      ) : null}
      <Textarea
        label="Your answer"
        autosize
        minRows={2}
        maxRows={6}
        value={blocked === null ? body : ""}
        disabled={blocked !== null}
        {...(blocked === null ? {} : { description: blocked.fix })}
        onChange={(event) => {
          setBody(event.currentTarget.value);
        }}
      />
      {blocked === null ? (
        <Text size="xs" c="dimmed">
          Sent from the church's number within a minute, with how to stop at the end.
        </Text>
      ) : null}
      {reply.isError ? (
        <Alert color={semanticColor("danger")} variant="light">
          {reply.error.message}
        </Alert>
      ) : null}
      <Button
        style={{ alignSelf: "flex-end" }}
        loading={reply.isPending}
        disabled={blocked !== null || text === ""}
        onClick={() => {
          reply.mutate(
            { conversationId: c.id, body: text },
            {
              onSuccess: () => {
                setBody("");
              },
            },
          );
        }}
      >
        Send
      </Button>
    </Stack>
  );
}

const names = z.object({
  firstName: z.string().trim().min(1, "A first name is needed").max(100),
  lastName: z.string().trim().min(1, "A last name is needed").max(100),
});

/** "Add this person" from an unknown number: the number they texted from (kept 30 days, D20) and a name. */
function AddPerson({
  conversationId,
  onDone,
}: {
  conversationId: string;
  /** The person added and linked, or null when nothing was (cancelled, or a colleague added them first). */
  onDone: (added: { readonly personId: string; readonly name: string } | null) => void;
}) {
  const number = useUnknownNumber(conversationId);
  const add = useAddPersonFromConversation();
  // The person a failed attempt already made, so a retry does not make them twice (#353 review). Their name is fixed
  // from then on: the retry links them as they were made.
  const [made, setMade] = useState<{ readonly id: string; readonly name: string } | null>(null);
  const form = useForm({
    initialValues: { firstName: "", lastName: "" },
    validate: zod4Resolver(names),
  });
  if (number.isPending) return <LoadingState label="Reading the number" />;
  if (number.isError)
    return <ErrorState message="Could not read the number they texted from. Please try again." />;
  const phone = number.data;
  return (
    <form
      onSubmit={form.onSubmit((values) => {
        const name = `${values.firstName.trim()} ${values.lastName.trim()}`;
        add.mutate(
          {
            conversationId,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            phone,
            personId: made?.id ?? null,
          },
          {
            onSuccess: (personId) => {
              setMade(null);
              notifications.show({
                color: semanticColor("success"),
                title: "Added",
                message: `${name} is added, and this conversation is on their record.`,
              });
              onDone({ personId, name });
            },
            onError: (error) => {
              if (error instanceof AddPersonError && error.alreadyLinked) {
                setMade(null);
                notifications.show({
                  color: semanticColor("info"),
                  title: "Already added",
                  message: "Someone has already added this person.",
                });
                onDone(null);
              } else if (error instanceof AddPersonError && error.personId !== null) {
                setMade((current) => current ?? { id: error.personId ?? "", name });
              }
            },
          },
        );
      })}
    >
      <Stack gap="sm">
        {phone === null ? (
          <Text size="sm">
            Their number is no longer kept: an unknown number is kept for 30 days, then only a
            fingerprint of it. The person is added without a number; add it on their record.
          </Text>
        ) : (
          <Stack gap={2}>
            <Text size="xs" c="dimmed">
              Number they texted from
            </Text>
            <Text size="sm">{formatPhone(phone)}</Text>
          </Stack>
        )}
        <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
          <TextInput
            label="First name"
            required
            readOnly={made !== null}
            {...form.getInputProps("firstName")}
          />
          <TextInput
            label="Last name"
            required
            readOnly={made !== null}
            {...form.getInputProps("lastName")}
          />
        </SimpleGrid>
        {made === null ? null : (
          <Text size="sm">
            {made.name} is already made: trying again links them to this conversation. Correct the
            name on their record afterwards if needed.
          </Text>
        )}
        <Text size="xs" c="dimmed">
          Texting the church is not agreeing to be texted: record their consent on their record if
          they give it.
        </Text>
        {add.isError && !(add.error instanceof AddPersonError && add.error.alreadyLinked) ? (
          <Alert color={semanticColor("danger")} variant="light">
            {add.error.message}
          </Alert>
        ) : null}
        <Group justify="flex-end">
          <Button
            variant="subtle"
            onClick={() => {
              onDone(null);
            }}
          >
            Cancel
          </Button>
          <Button type="submit" loading={add.isPending}>
            Add and link
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
